// src/tattoo/legal/PrivacyPage.jsx
//
// Public-facing privacy policy at /privacy. Mirrors the source of truth in
// docs/PRIVACY.md — when that doc changes, this page must change with it.
// Hand-rendered as JSX (no markdown library) because the policy is short
// and stable enough that a runtime markdown parser would be overkill.
//
// The route is NOT wrapped in ProtectedRoute. A privacy policy must be
// reachable to users who have not signed in (and to crawlers, app-store
// reviewers, regulators, etc.) — gating it behind auth would defeat the
// purpose of having one.

import React from "react";
import { Link, useNavigate } from "react-router-dom";

const LAST_UPDATED = "April 23, 2026";
const CONTACT_EMAIL = "privacy@tattune.ca";

export default function PrivacyPage() {
  const navigate = useNavigate();

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "#0a0a0a",
        color: "#eaeaea",
        padding: "32px 20px 80px",
      }}
    >
      <div style={{ maxWidth: 720, margin: "0 auto" }}>
        <button
          type="button"
          onClick={() => {
            // navigate(-1) when there's history so we land back where the
            // user came from (sign-in card, in-app footer, etc.). On a
            // direct deep link / fresh tab, fall back to the landing.
            if (window.history.length > 1) {
              navigate(-1);
            } else {
              navigate("/");
            }
          }}
          style={{
            background: "transparent",
            border: "1px solid #2a2a2a",
            color: "#d7d7d7",
            padding: "6px 12px",
            borderRadius: 8,
            cursor: "pointer",
            fontSize: 13,
            marginBottom: 24,
          }}
        >
          ← Back
        </button>

        <h1
          style={{
            fontSize: 28,
            margin: "0 0 6px",
            letterSpacing: "0.01em",
          }}
        >
          Privacy policy
        </h1>
        <p style={{ fontSize: 13, color: "#888", margin: "0 0 28px" }}>
          Last updated: {LAST_UPDATED}
        </p>

        <Lede>
          This page is a plain-English summary of how Tattune handles personal
          data submitted through the platform. It is not legal advice and
          does not substitute for the formal Terms of Service.
        </Lede>

        <Lede>
          The sections below focus on the parts of Tattune that move
          personal data between customers, artists, and the platform —
          primarily inquiries, quote-pool requests, and the artist-side
          Leads Inbox.
        </Lede>

        <Section title="What we collect">
          <P>
            When a customer submits a tattoo inquiry through Tattune, we
            receive:
          </P>
          <Ul>
            <li>
              The contact details they choose to share — typically name,
              email address, and optionally a phone number.
            </li>
            <li>The design they uploaded or composed in the editor.</li>
            <li>
              Any free-text notes attached to the inquiry (placement, budget,
              availability, story).
            </li>
            <li>
              A best-effort city/region/country location string when
              submitting to the open quote pool.
            </li>
          </Ul>
          <P>
            When an artist signs up, we additionally collect the public
            profile fields they enter into the studio wizard — display
            name, styles, location, website, Instagram, and contact email.
          </P>
          <P>
            We do <strong>not</strong> sell personal data. We do{" "}
            <strong>not</strong> share inquiry contents with anyone other
            than the artist or studio the customer addressed it to (or, for
            pool requests, the artists whose preferences match it).
          </P>
        </Section>

        <Section title="How long we keep inquiry data">
          <P>
            <strong>
              We retain inquiry and quote-request data indefinitely
            </strong>{" "}
            so artists can reference past conversations with returning
            customers. Returning clients are common in tattooing, and
            artists routinely want to look back at a previous design idea,
            location, or quote when planning the next session.
          </P>
          <P>Indefinite retention applies to:</P>
          <Ul>
            <li>Direct inquiries to a specific shop.</li>
            <li>Open quote-pool requests.</li>
            <li>Any images attached to those inquiries.</li>
            <li>Artist-private notes the recipient artist saved against an inquiry.</li>
          </Ul>
          <P>We do not run automatic deletion jobs against this data.</P>
        </Section>

        <Section title="Deletion on request">
          <P>
            You may request deletion of your data at any time by emailing{" "}
            <a
              href={`mailto:${CONTACT_EMAIL}`}
              style={linkStyle}
            >
              {CONTACT_EMAIL}
            </a>
            . We will delete or anonymize your data across all shops you've
            inquired with within{" "}
            <strong>14 calendar days</strong> of the request.
          </P>
          <P>Notes:</P>
          <Ul>
            <li>
              Deletion requests cascade across all shops, not just one. You
              don't have to email each artist individually.
            </li>
            <li>
              If you originally inquired with three different studios, your
              contact information will be removed from all three — even
              though those studios do not see each other's lead lists.
            </li>
            <li>
              Artists' private notes on an inquiry will be removed alongside
              the inquiry itself; they cannot survive the deletion of the
              lead row.
            </li>
          </Ul>
          <P>
            We acknowledge each request when received and confirm completion
            in writing. Anonymization (preserving aggregate stats but
            removing personal identifiers) is offered as an alternative when
            full deletion would disrupt an in-progress conversation.
          </P>
        </Section>

        <Section title="What happens when an artist closes their account">
          <P>When an artist deletes their Tattune account:</P>
          <Ul>
            <li>Their shop record is deleted.</li>
            <li>
              Direct leads addressed to that shop are deleted automatically.
            </li>
            <li>
              The artist's per-request notes and statuses on pool requests
              are deleted with the shop.
            </li>
            <li>
              The underlying pool quote request itself is{" "}
              <strong>not</strong> deleted, because it may still be visible
              to other artists. The customer's data within it persists under
              the same indefinite retention rule above and can be removed
              via the deletion-request flow.
            </li>
          </Ul>
        </Section>

        <Section title="Email and the Leads Inbox">
          <P>
            When a customer submits a direct inquiry, we send a confirmation
            email to the customer and a notification email to the artist.
            The customer's email is used:
          </P>
          <Ol>
            <li>To send their own confirmation copy.</li>
            <li>
              As the <code style={codeStyle}>Reply-To:</code> address on the
              artist's notification, so the artist replies directly to the
              customer if they want to follow up.
            </li>
          </Ol>
          <P>
            Artists can set their pool notification preference to "inbox
            only" — in that mode, pool requests still appear in their in-app
            inbox but do not trigger an email. Direct inquiries always send
            both confirmation emails. Customer email addresses are never
            shared with the wider pool or used for marketing.
          </P>
        </Section>

        <Section title="Security">
          <Ul>
            <li>
              Row-level security on our database ensures artists can only
              read their own shop's leads and their own pool-request views.
            </li>
            <li>
              Service-role tokens are scoped to server-side functions and
              never exposed to the browser.
            </li>
            <li>
              Customer-facing share links expose only the data the
              originator chose to share.
            </li>
          </Ul>
        </Section>

        <Section title="Contact">
          <P>
            Questions or deletion requests:{" "}
            <a
              href={`mailto:${CONTACT_EMAIL}`}
              style={linkStyle}
            >
              {CONTACT_EMAIL}
            </a>
            .
          </P>
          <P>
            We aim to respond within two business days and to complete
            deletion requests within 14 calendar days.
          </P>
        </Section>

        <hr
          style={{
            border: 0,
            borderTop: "1px solid #1f1f1f",
            margin: "40px 0 20px",
          }}
        />
        <p style={{ fontSize: 12, color: "#666", textAlign: "center" }}>
          <Link to="/" style={linkStyle}>
            Back to Tattune
          </Link>
        </p>
      </div>
    </div>
  );
}

// ---- presentational atoms -------------------------------------------------

function Section({ title, children }) {
  return (
    <section style={{ marginBottom: 32 }}>
      <h2
        style={{
          fontSize: 18,
          fontWeight: 600,
          margin: "0 0 12px",
          color: "#f3f3f3",
          letterSpacing: "0.01em",
        }}
      >
        {title}
      </h2>
      {children}
    </section>
  );
}

function P({ children }) {
  return (
    <p
      style={{
        fontSize: 14,
        lineHeight: 1.65,
        color: "#cfcfcf",
        margin: "0 0 12px",
      }}
    >
      {children}
    </p>
  );
}

function Lede({ children }) {
  return (
    <p
      style={{
        fontSize: 14,
        lineHeight: 1.65,
        color: "#a8a8a8",
        margin: "0 0 14px",
      }}
    >
      {children}
    </p>
  );
}

function Ul({ children }) {
  return (
    <ul
      style={{
        margin: "0 0 12px",
        paddingLeft: 22,
        fontSize: 14,
        lineHeight: 1.65,
        color: "#cfcfcf",
      }}
    >
      {children}
    </ul>
  );
}

function Ol({ children }) {
  return (
    <ol
      style={{
        margin: "0 0 12px",
        paddingLeft: 22,
        fontSize: 14,
        lineHeight: 1.65,
        color: "#cfcfcf",
      }}
    >
      {children}
    </ol>
  );
}

const linkStyle = {
  color: "#e57373",
  textDecoration: "underline",
};

const codeStyle = {
  background: "#1a1a1a",
  border: "1px solid #2a2a2a",
  padding: "1px 6px",
  borderRadius: 4,
  fontSize: 13,
  fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
};
